import {
  ChangeDetectorRef,
  Component,
  computed,
  OnInit,
  signal,
  Signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommunicationService } from '../../services/communication.service';
import { QuotaManagementService } from '../../services/quota-management.service';
import { MessageService } from '../../services/message.service';

@Component({
  selector: 'manage-quotas',
  templateUrl: './manage-quotas.component.html',
  styleUrl: './manage-quotas.component.css',
  encapsulation: ViewEncapsulation.None,
})
export class ManageQuotasComponent implements OnInit {
  isSuperUser: boolean;
  isParentOrgSuperUser = false;
  quotaType: '';
  organisationQuotaDetails: Record<string, any>;
  teamQuotaDetails: Record<string, any>;
  teamQuotaCache: Record<string, any> = {};
  orgQuotaAllocated: WritableSignal<number> = signal(0);
  orgRemainingQuota: Signal<number> = signal(0);

  loadingStatus: any;

  constructor(
    private route: ActivatedRoute,
    private communicationService: CommunicationService,
    private quotaManagementService: QuotaManagementService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    document.title = 'Manage Quota';
    const queryParams = this.route.snapshot.queryParams;
    this.quotaType = queryParams['type'] || 'Magic Insights';
    this.loadingStatus = this.quotaManagementService.isLoading;
    this.communicationService.getIsSuperUserMessage().subscribe(isSuperUser => {
      this.isSuperUser = isSuperUser;
    });
    this.communicationService
      .getIsParentOrgSuperUserMessage()
      .subscribe(isParentOrgSuperUser => {
        this.isParentOrgSuperUser = isParentOrgSuperUser;
        if (this.isParentOrgSuperUser) {
          this.getOrgQuotaDetails();
        } else if (this.isSuperUser) {
          this.getTeamDetails('');
        } else {
          // todo need to be able to view my quota maybe?
        }
      });
  }

  getOrgQuotaDetails() {
    this.quotaManagementService
      .getOrganisationQuotaDetails(this.quotaType)
      .subscribe(data => {
        if (data) {
          this.organisationQuotaDetails = data;
          this.orgQuotaAllocated.set(
            data.teams.reduce((total, team) => (total += team.quota), 0)
          );
          this.orgRemainingQuota = computed(
            () =>
              this.organisationQuotaDetails.organisation_quota -
              this.orgQuotaAllocated()
          );
        }
      });
  }

  getTeamDetails(teamId: number | string, reFetch = false): void {
    if (!reFetch && this.teamQuotaCache[teamId]) {
      this.teamQuotaDetails = this.teamQuotaCache[teamId];
      return;
    }
    this.quotaManagementService
      .getTeamQuotaDetails(this.quotaType, teamId)
      .subscribe(data => {
        if (data) {
          this.teamQuotaDetails = data;
          this.teamQuotaDetails['currentTeamTotal'] = data.users.reduce(
            (count, user) => (count += user.quota),
            0
          );
          this.teamQuotaCache[teamId] = this.teamQuotaDetails;
          this.cdr.detectChanges();
        }
      });
  }

  setTeamQuota(
    teamId: number,
    newQuota: number | string,
    currentTeamQuota: number | string
  ): void {
    if (Number(newQuota) < 0) {
      this.messageService.showAndHideAfterDelay(
        'Please enter a positive integer',
        3100
      );
      return;
    }

    if (Number(newQuota) === Number(currentTeamQuota)) return;
    const updatedQuota = Number(newQuota) - Number(currentTeamQuota);
    this.quotaManagementService
      .setTeamQuota(this.quotaType, teamId, newQuota)
      .subscribe(data => {
        if (data.success) {
          this.messageService.showInfo('Team quota updated', 4000);
          this.orgQuotaAllocated.update(current => current + updatedQuota);
          this.getTeamDetails(teamId, true);
          this.organisationQuotaDetails.teams.forEach(team => {
            if (team.id === teamId) {
              team.quota = newQuota;
            }
          });
        }
      });
  }

  setUserQuota(userId: number, quota: number, teamId?: number): void {
    if (quota < 0) {
      this.messageService.showAndHideAfterDelay(
        'Please enter a positive integer',
        3100
      );
      return;
    }
    this.quotaManagementService
      .setUserQuota(this.quotaType, userId, quota)
      .subscribe(data => {
        if (data.success) {
          this.messageService.showInfo('User quota updated', 4000);
          this.getTeamDetails(teamId, true);
        }
      });
  }

  equallyDistributeQuotas(): void {
    this.quotaManagementService
      .equallyDistributeQuotas(this.quotaType)
      .subscribe(data => {
        if (data.success) {
          this.messageService.showInfo('Quotas Equally Distributed. ', 4000);
          this.quotaManagementService
            .getOrganisationQuotaDetails(this.quotaType)
            .subscribe(data => {
              this.organisationQuotaDetails = data;
            });
        }
      });
  }

  getProgressBarValue(currentTotal: number, assignedTotal: number): number {
    return (currentTotal / assignedTotal) * 100;
  }
}
